import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  useTheme,
  MenuItem,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { addTenants, updateTenants } from "state/apis";
import { toast } from "react-toastify";
import Autocomplete from "@mui/material/Autocomplete";
import { Close as CloseIcon } from "@mui/icons-material";
import { geoData } from "state/geoData";

const countryList = geoData.features
  .map((f) => f.properties.name)
  .sort((a, b) => a.localeCompare(b));

const AddTenantsForm = ({ open, onClose, onSubmit, editData, editMode }) => {
  const theme = useTheme();
  const [selectedCountry, setSelectedCountry] = useState("");
  const [data, setData] = useState({
    name: "",
    nationality: "",
    contact1: "",
    contact2: "",
    contact3: "",
    emiratesId: "",
    docs: [],
  });

  useEffect(() => {
    if (editMode && editData) {
      setData((prevData) => ({
        ...prevData,
        name: editData.name,
        nationality: editData.nationality,
        contact1: editData.contact1 || "",
        contact2: editData.contact2 || "",
        contact3: editData.contact3 || "",
        emiratesId: editData.emiratesId,
      }));
      setSelectedCountry(editData.nationality);
    } else {
      setData({
        name: "",
        nationality: "",
        contact1: "",
        contact2: "",
        contact3: "",
        emiratesId: "",
        docs: [],
      });
      setSelectedCountry("");
    }
  }, [editData, editMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCountryChange = (event, newValue) => {
    setSelectedCountry(newValue);
    setData((prevData) => ({
      ...prevData,
      nationality: newValue,
    }));
  };

  // document
  const handleAddDocument = () => {
    setData((prevData) => ({
      ...prevData,
      docs: [...prevData.docs, { name: "", file: null }],
    }));
  };

  const handleDocumentNameChange = (index, value) => {
    const updatedDocuments = [...data.docs];
    updatedDocuments[index].name = value;
    setData((prevData) => ({
      ...prevData,
      docs: updatedDocuments,
    }));
  };
  const handleCancelDocument = (indexToRemove) => {
    setData((prevState) => ({
      ...prevState,
      docs: prevState.docs.filter((_, index) => index !== indexToRemove),
    }));
  };
  const handleDocumentFileChange = (index, file) => {
    const updatedDocuments = [...data.docs];
    updatedDocuments[index].file = file;
    setData((prevData) => ({
      ...prevData,
      docs: updatedDocuments,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        const response = await updateTenants(editData.id, data);
        toast.success(response.message);
      } else {
        const response = await addTenants(data);
        toast.success(response.message);
      }
      onSubmit();
      onClose();
    } catch (error) {
      console.error("Error:", error);
      if (error.response) {
        toast.error(error.response.data.message);
      } else if (error.request) {
        toast.error("No response received from server.");
      } else {
        toast.error("An error occurred. Please try again.");
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      {/* <DialogTitle sx={{ textAlign: "center", fontWeight: "900", mb: "2px" }}>
                {editMode ? "Edit Tenant" : "Add Tenant"}
            </DialogTitle> */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: "bold",
          marginLeft: "6px",
        }}
      >
        {editMode ? "Edit Tenant" : "Add Tenant"}
        <IconButton onClick={onClose} sx={{ color: "inherit" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} p={"5px"}>
          <Grid item xs={12}>
            <TextField
              color="primary"
              fullWidth
              label="Name"
              name="name"
              value={data.name}
              onChange={handleChange}
            />
          </Grid>
          {/* <Grid item xs={12}>
                        <TextField
                            select
                            color="primary"
                            fullWidth
                            label="Select country"
                            name="nationality" 
                            value={data.nationality}
                            onChange={handleChange}
                            required
                        >
                            {countries.map((val, index) => (
                                <MenuItem key={index} value={val}>
                                    {val}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid> */}
          <Grid item xs={12}>
            <Autocomplete
              disablePortal
              id="combo-box-demo"
              options={countryList}
              value={selectedCountry}
              onChange={handleCountryChange}
              isOptionEqualToValue={(option, value) => option === value}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
                "& .MuiInputLabel-root": {
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                },
              }}
              renderInput={(params) => <TextField {...params} fullWidth label="Select country" />}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              color="primary"
              fullWidth
              label="Contact 1"
              name="contact1"
              value={data.contact1}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              color="primary"
              fullWidth
              label="Contact 2"
              name="contact2"
              value={data.contact2}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              color="primary"
              fullWidth
              label="Email"
              name="contact3"
              value={data.contact3}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              color="primary"
              fullWidth
              label="Emirate Id"
              name="emiratesId"
              value={data.emiratesId}
              onChange={handleChange}
            />
          </Grid>
          <Grid
            item
            xs={12}
            m={3}
            mb={1}
            mr={0}
            borderRadius={1}
            border={1}
            p={2}
            sx={{ borderColor: "grey.400" }}
          >
            <Grid container alignItems="center">
              <Grid item xs={8}>
                <label style={{ color: theme.palette.grey[600] }}>Tenant Documents</label>
              </Grid>
              <Grid item xs={4} container justifyContent="flex-end">
                <Button variant="contained" onClick={handleAddDocument}>
                  Add Document
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              {data.docs.map((document, index) => (
                <Grid container spacing={2} key={index} p={1}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      color="primary"
                      label="Document Name"
                      value={document.name}
                      onChange={(e) => handleDocumentNameChange(index, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={5} margin={"auto"}>
                    <input
                      type="file"
                      accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => handleDocumentFileChange(index, e.target.files[0])}
                    />
                  </Grid>
                  <Grid item xs={3} container justify="end" margin="auto">
                    <IconButton color="error" onClick={() => handleCancelDocument(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <DialogActions>
          {/* <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            color: theme.palette.primary,
                            backgroundColor: theme.palette.primary[800],
                        }}
                    >
                        Cancel
                    </Button> */}
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              color: theme.palette.primary.light,
              backgroundColor: theme.palette.secondary[200],
            }}
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default AddTenantsForm;
